import React, { useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { Permission } from '../permissions';
import { useApi } from '../useApi';
import ConfirmationModal from '../components/ConfirmationModal';
import { useDevicePosition } from './useDevicePosition';
import { useDirectoryNearby } from './useDirectoryNearby';
import {
  sectionCourses, countUnlocated, usesMiles, formatRadius,
} from '../utils/courseDistance';

/**
 * Everything a course picker needs to draw <CourseSections>, for both pickers.
 *
 * `courseAnchor` ({lat, lng, courseId, name, round}) is the multi-round case:
 * from Round 2 on, the question is "near where we'll be", not "near where I'm
 * sitting while I plan the away weekend", so it is measured from the latest
 * round's course by default — with the phone one tap away.
 *
 * `today`: the round is today, the only time "You're here" can be said.
 *
 * `onAdopted(course)`: a course manager also sees directory courses near the
 * same point that the society hasn't got yet (`near.directory`). So does a
 * MEMBER where `membersMayAdd` — the quick-round picker only: a member out at
 * a course the society hasn't got could otherwise not start a round there
 * without finding an admin. Everywhere else stays with course managers. Adding one
 * copies it into the society's list and hands the new course here, so the
 * picker carries straight on to choosing a tee — adopt-and-pick is one flow.
 * The confirmation lives in `adoptDialog`, which the picker renders OUTSIDE
 * its dropdown: Step1's list closes on any tap outside it, and a modal
 * inside the list would close with it mid-tap.
 */
export function useNearbyCourses({
  courses, search = '', today = false, courseAnchor = null, onAdopted = null,
  membersMayAdd = false,
}) {
  const { status, position, request } = useDevicePosition();
  const { hasPermission, isSocietyOwner } = useAuth();
  const [from, setFrom] = useState('course');

  const deviceOffered = status !== 'denied' && status !== 'unavailable';
  const fromCourse = !!courseAnchor && (from === 'course' || !deviceOffered);
  const deviceAnchor = status === 'ready' ? position : null;

  const anchor = fromCourse
    ? { lat: courseAnchor.lat, lng: courseAnchor.lng, courseId: courseAnchor.courseId,
        label: `Round ${courseAnchor.round} course` }
    : deviceAnchor;

  const sections = useMemo(
    () => sectionCourses({ courses, anchor, today: today && !fromCourse, search }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courses, search, today, fromCourse, anchor?.lat, anchor?.lng, anchor?.accuracy, anchor?.courseId]);

  const miles = usesMiles(typeof navigator !== 'undefined' ? navigator.language : 'en-GB');
  const canFixCourses = isSocietyOwner || hasPermission(Permission.MANAGE_COURSES);
  const unlocated = countUnlocated(courses);

  const canAddFromDirectory = canFixCourses
    || (membersMayAdd && hasPermission(Permission.CREATE_QUICK_ROUND));
  const directory = useDirectoryNearby({
    anchor,
    enabled: canAddFromDirectory && !!onAdopted && !sections.searching,
  });
  const { authenticatedFetch } = useApi();
  const [adopting, setAdopting] = useState(null);   // the directory row being confirmed
  const [adoptError, setAdoptError] = useState(null);

  const adopt = async () => {
    const row = adopting;
    setAdopting(null);
    setAdoptError(null);
    try {
      const res = await authenticatedFetch(`/global-courses/${row.id}/adopt`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      // 409: the society already had it (a second device, a double tap) and
      // the server hands back that copy, which is just as good to pick.
      const course = res.ok ? body : (res.status === 409 ? body.course : null);
      if (!course) throw new Error(body.error || `HTTP ${res.status}`);
      directory.remove(row.id);
      onAdopted(course);
    } catch (e) {
      setAdoptError(`Couldn't add ${row.name} — try again.`);
    }
  };

  const adoptDialog = (
    <ConfirmationModal
      isOpen={!!adopting}
      title="Add to your society?"
      message={adopting
        ? `${adopting.name} will be copied from the course directory, with its tees, into your society's course list.`
        : ''}
      confirmText="Add course"
      cancelText="Cancel"
      onConfirm={adopt}
      onCancel={() => setAdopting(null)}
    />
  );

  let nearStatus = fromCourse ? 'ready' : status;
  // Switched to "Me" from a course anchor: say it's working rather than
  // dropping the whole section (and its switch) for a moment.
  if (courseAnchor && !fromCourse && status === 'checking') nearStatus = 'locating';

  const control = courseAnchor && deviceOffered ? (
    <span className="course-sections__seg" role="group" aria-label="Measure distance from">
      <button type="button" aria-pressed={fromCourse} onClick={() => setFrom('course')}>
        Round {courseAnchor.round}
      </button>
      <button type="button" aria-pressed={!fromCourse} onClick={() => setFrom('me')}>
        Me
      </button>
    </span>
  ) : null;

  const near = {
    status: nearStatus,
    // The switch beside it says which round; the heading has the room for a name.
    heading: fromCourse ? `Near ${courseAnchor.name}` : 'Near you',
    control,
    onRequest: request,
    emptyText: `No courses within ${formatRadius(miles)}`,
    directory: {
      rows: directory.rows,
      miles,
      error: adoptError,
      onAdd: (row) => { setAdoptError(null); setAdopting(row); },
    },
    // Only someone who can fix it is told; a member can do nothing about it.
    note: canFixCourses && unlocated
      ? `${unlocated} of your courses ${unlocated === 1 ? 'has' : 'have'} no location — add a postcode in Courses to include ${unlocated === 1 ? 'it' : 'them'}.`
      : null,
  };

  return { sections, near, miles, adoptDialog };
}
